import React, { useState, useEffect, useCallback, useMemo } from 'react';
import StatCard from '../../components/charts/StatCard';
import DonutChart from '../../components/charts/DonutChart';
import { useSettings } from '../../contexts/SettingsContext';
import { aiAutomationApi } from '../../utils/aiAutomationApi';

// ── Date helpers ──
const formatDate = (d) => d.toISOString().split('T')[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
// Show cost with enough precision — $0.00 is confusing, show $0.0013 instead
const fmtCost = (v) => {
  if (v == null || v === 0) return '$0.00';
  if (v >= 0.01) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};
const fmtDurationSeconds = (entry = {}) => {
  const seconds = entry.duration_seconds ?? (entry.ms ? entry.ms / 1000 : null);
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const value = Number(seconds);
  if (value < 1) return `${value.toFixed(2)}s`;
  if (value < 10) return `${value.toFixed(1)}s`;
  return `${Math.round(value)}s`;
};

const DATE_RANGES = [
  { label: 'Last 7 days', start: () => daysAgo(7), end: () => new Date() },
  { label: 'Last 30 days', start: () => daysAgo(30), end: () => new Date() },
  { label: 'Last 90 days', start: () => daysAgo(90), end: () => new Date() },
  { label: 'This month', start: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1), end: () => new Date() },
];

// ── Friendly names for AI features (endpoints) ──
const ENDPOINT_LABELS = {
  search_intent_detection: 'Understanding Search',
  intent_classifier: 'Understanding Chat Messages',
  lifecycle_sql_generator: 'Finding Data',
  lifecycle_humanizer: 'Writing Responses',
  jd_candidate_matcher_score: 'Matching Candidates',
  ask_mode: 'Answering Questions',
  search_skill_inference: 'Detecting Skills',
  search_skill_extraction: 'Extracting Skills',
  search_location_validation: 'Validating Locations',
  search_field_extraction: 'Extracting Search Filters',
  search_field_detection: 'Detecting Search Filters',
  search_spelling_correction: 'Fixing Spelling',
  health_check: 'Health Check',
  embedding: 'Understanding Text',
  notetaker_transcription: 'Meeting Transcription',
  notetaker_highlight_analysis: 'Interview Highlights',
  unknown: 'Other',
};
const friendlyEndpoint = (ep) => ENDPOINT_LABELS[ep] || ep.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ── Simple bar ──
const ProportionBar = ({ value, max, color, height = 'h-2' }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className={`w-full bg-gray-100 dark:bg-gray-800 rounded-full ${height}`}>
      <div className={`${height} rounded-full transition-all duration-500`} style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

// ── Status badge ──
const StatusBadge = ({ status }) => {
  const map = {
    success: { label: 'Success', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    quota_exceeded: { label: 'No Credits', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    error: { label: 'Failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  };
  const s = map[status] || map.error;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.label}</span>;
};

const SOURCE_LABELS = {
  platform: 'Platform',
  notetaker: 'Platform Notetaker',
};

const normalizeUsageSource = (entry = {}) => {
  const explicitSource = String(entry.source || '').toLowerCase();
  if (explicitSource === 'notetaker' || explicitSource === 'platform_notetaker' || explicitSource === 'platform-notetaker') {
    return 'notetaker';
  }

  const sourceHost = String(entry.source_host || '').toLowerCase();
  if (sourceHost.startsWith('notetaker:')) {
    return 'notetaker';
  }

  return 'platform';
};

const friendlySource = (value) => SOURCE_LABELS[value] || value.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const SourceBadge = ({ source }) => {
  const normalized = normalizeUsageSource({ source });
  const map = {
    platform: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    notetaker: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  };

  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[normalized] || map.platform}`}>{friendlySource(normalized)}</span>;
};

// ── Main ──
export default function AdminSitePage({ usageSource = '' }) {
  const { settings } = useSettings();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings?.theme?.colors?.secondary || '#20C997';

  const [dateRangeIdx, setDateRangeIdx] = useState(1);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [endpointFilter, setEndpointFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');

  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entriesTotal, setEntriesTotal] = useState(0);
  const [entriesPage, setEntriesPage] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [cyclesData, setCyclesData] = useState(null);
  const [cyclesLoading, setCyclesLoading] = useState(false);
  const [expandedCycle, setExpandedCycle] = useState(null);
  const [liveKeyStatus, setLiveKeyStatus] = useState(null); // live openai key check

  const dateRange = useMemo(() => {
    if (customStart && customEnd) return { start: customStart, end: customEnd };
    const range = DATE_RANGES[dateRangeIdx];
    return { start: formatDate(range.start()), end: formatDate(range.end()) };
  }, [dateRangeIdx, customStart, customEnd]);

  const effectiveSource = usageSource || sourceFilter;

  const companyMap = useMemo(() => {
    const map = {};
    companies.forEach((c) => { map[c.id] = c; });
    return map;
  }, [companies]);

  const getCompanyInfo = (cid) => {
    if (!cid || cid === 0) return { name: 'Shared', email: '' };
    const c = companyMap[cid];
    return c ? { name: c.name || `#${cid}`, email: c.email || '' } : { name: `#${cid}`, email: '' };
  };

  // ── Fetchers ──
  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
      if (companyFilter) p.append('company_id', companyFilter);
      if (effectiveSource) p.append('source', effectiveSource);
      if (providerFilter) p.append('provider', providerFilter);
      setSummary(await aiAutomationApi.get(`/openai/usage/summary?${p}`));
    } catch (err) { console.error('Failed to fetch usage summary:', err); }
    finally { setLoading(false); }
  }, [dateRange, companyFilter, effectiveSource, providerFilter]);

  const fetchEntries = useCallback(async (page = 1) => {
    setEntriesLoading(true);
    try {
      const p = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end, page: String(page), page_size: '50' });
      if (companyFilter) p.append('company_id', companyFilter);
      if (effectiveSource) p.append('source', effectiveSource);
      if (providerFilter) p.append('provider', providerFilter);
      if (endpointFilter) p.append('endpoint', endpointFilter);
      if (modelFilter) p.append('model', modelFilter);
      const res = await aiAutomationApi.get(`/openai/usage/entries?${p}`);
      setEntries(res.data || []); setEntriesTotal(res.total || 0); setEntriesPage(page);
    } catch (err) { console.error('Failed to fetch entries:', err); }
    finally { setEntriesLoading(false); }
  }, [dateRange, companyFilter, endpointFilter, modelFilter, effectiveSource, providerFilter]);

  const fetchCycles = useCallback(async () => {
    setCyclesLoading(true);
    try {
      const p = new URLSearchParams();
      if (companyFilter) p.append('company_id', companyFilter);
      if (effectiveSource) p.append('source', effectiveSource);
      if (providerFilter) p.append('provider', providerFilter);
      setCyclesData(await aiAutomationApi.get(`/openai/usage/cycles?${p}`));
    } catch (err) { console.error('Failed to fetch cycles:', err); }
    finally { setCyclesLoading(false); }
  }, [companyFilter, effectiveSource, providerFilter]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await aiAutomationApi.get('/openai/usage/companies');
      setCompanies(res.companies || []);
    } catch { /* ignore */ }
  }, []);

  const exportToExcel = useCallback(async () => {
    try {
      const p = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end, page: '1', page_size: '10000' });
      if (companyFilter) p.append('company_id', companyFilter);
      if (effectiveSource) p.append('source', effectiveSource);
      if (providerFilter) p.append('provider', providerFilter);
      if (endpointFilter) p.append('endpoint', endpointFilter);
      if (modelFilter) p.append('model', modelFilter);
      const res = await aiAutomationApi.get(`/openai/usage/entries?${p}`);
      const data = res.data || [];
      if (!data.length) { alert('No data to export'); return; }
      const headers = ['Time', 'Started At', 'Ended At', 'Company', 'Email', 'Source', 'Feature', 'Model', 'Input Tokens', 'Output Tokens', 'Total Tokens', 'Cost (USD)', 'Duration (sec)', 'Status'];
      const rows = data.map((e) => {
        const c = getCompanyInfo(e.company_id);
        return [e.ts || '', e.started_at || '', e.ended_at || e.ts || '', e.company_name || c.name, c.email, friendlySource(normalizeUsageSource(e)), friendlyEndpoint(e.endpoint || ''), e.model || '', e.prompt_tokens ?? 0, e.completion_tokens ?? 0, e.total_tokens ?? 0, e.cost ?? 0, e.duration_seconds ?? (e.ms ? (e.ms / 1000).toFixed(3) : ''), e.status === 'success' ? 'Success' : e.status === 'quota_exceeded' ? 'No Credits' : 'Failed'];
      });
      const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const prefix = effectiveSource ? `${effectiveSource}_` : '';
      const a = document.createElement('a'); a.href = url; a.download = `${prefix}ai_usage_${dateRange.start}_to_${dateRange.end}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed:', err); alert('Export failed.'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, companyFilter, endpointFilter, modelFilter, companyMap, effectiveSource, providerFilter]);

  // ── Live key check ──
  const fetchKeyStatus = useCallback(async () => {
    try {
      const res = await aiAutomationApi.get('/openai/check');
      setLiveKeyStatus(res);
    } catch { setLiveKeyStatus(null); }
  }, []);

  useEffect(() => { if (!usageSource) fetchCompanies(); fetchKeyStatus(); }, [fetchCompanies, fetchKeyStatus, usageSource]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { if (activeTab === 'entries') fetchEntries(1); }, [activeTab, fetchEntries]);
  useEffect(() => { if (activeTab === 'cycles') fetchCycles(); }, [activeTab, fetchCycles]);

  const endpointDonutData = useMemo(() => (summary?.by_endpoint || []).map((e) => ({ name: friendlyEndpoint(e.endpoint), count: e.calls })), [summary]);
  const modelDonutData = useMemo(() => (summary?.by_model || []).map((m) => ({ name: m.provider ? `${m.provider}/${m.model}` : m.model, count: m.calls })), [summary]);
  const providerDonutData = useMemo(() => (summary?.by_provider || []).map((p) => ({ name: p.provider?.charAt(0).toUpperCase() + p.provider?.slice(1), count: p.calls })), [summary]);
  const sourceDonutData = useMemo(() => (summary?.by_source || []).map((s) => ({ name: friendlySource(s.source), count: s.calls })), [summary]);
  const tokenSplitData = useMemo(() => {
    const p = summary?.totals?.prompt_tokens || 0, c = summary?.totals?.completion_tokens || 0;
    return (p + c === 0) ? [] : [{ name: 'Input (sent to AI)', count: p }, { name: 'Output (AI response)', count: c }];
  }, [summary]);

  const analysisData = useMemo(() => {
    if (!summary) return null;
    const trend = summary.daily_trend || [];
    const tc = summary.totals?.calls || 0, tt = summary.totals?.tokens || 0, tco = summary.totals?.cost || 0;
    const days = trend.length || 1;
    let peakDay = null, peakCalls = 0;
    for (const d of trend) { if (d.calls > peakCalls) { peakCalls = d.calls; peakDay = d.date; } }
    return {
      avgCallsPerDay: Math.round(tc / days), avgTokensPerCall: tc > 0 ? Math.round(tt / tc) : 0,
      avgCostPerDay: tco / days, peakDay, peakCalls,
      topEndpoint: summary.by_endpoint?.[0] || null, topModel: summary.by_model?.[0] || null,
      endpointsByCost: [...(summary.by_endpoint || [])].sort((a, b) => b.cost - a.cost),
      modelsByCost: [...(summary.by_model || [])].sort((a, b) => b.cost - a.cost),
      tokensPerDollar: tco > 0 ? Math.round(tt / tco) : 0, days,
    };
  }, [summary]);

  const totalPages = Math.ceil(entriesTotal / 50);
  const selCompany = companyFilter ? companyMap[companyFilter] : null;

  // ── Computed: health status (uses live key check, NOT historical data) ──
  const healthStatus = useMemo(() => {
    if (!summary?.totals && !liveKeyStatus) return 'loading';
    // Live key check is the source of truth for current status
    if (liveKeyStatus?.error_code === 'quota_exceeded') return 'no_credits';
    if (liveKeyStatus?.error_code === 'invalid_key' || liveKeyStatus?.error_code === 'access_denied') return 'key_error';
    if (liveKeyStatus?.status === 'error' && liveKeyStatus?.error_code !== 'quota_exceeded') return 'issues';
    if (liveKeyStatus?.status === 'success') return 'healthy';
    // Fallback to summary if live check hasn't loaded
    if (summary?.totals?.errors > 0 && summary?.totals?.calls > 0 && (summary.totals.errors / summary.totals.calls) > 0.1) return 'issues';
    return 'healthy';
  }, [summary, liveKeyStatus]);

  const healthConfig = {
    loading: { color: '#9CA3AF', bg: 'bg-gray-50 dark:bg-gray-800/50', border: 'border-gray-200 dark:border-gray-700', label: 'Checking...', desc: 'Verifying AI service status...' },
    healthy: { color: '#10B981', bg: 'bg-green-50 dark:bg-green-900/10', border: 'border-green-200 dark:border-green-800', label: 'All Systems Working', desc: 'AI features are running normally. Your OpenAI key is valid and has credits.' },
    issues: { color: '#F59E0B', bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-800', label: 'Connection Issue', desc: liveKeyStatus?.message || `Some requests are failing. ${summary?.totals?.errors || 0} errors detected.` },
    no_credits: { color: '#EF4444', bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200 dark:border-red-800', label: 'OpenAI Credits Exhausted', desc: 'Your OpenAI balance is empty. Add credits at platform.openai.com to resume AI features.' },
    key_error: { color: '#EF4444', bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200 dark:border-red-800', label: 'API Key Problem', desc: liveKeyStatus?.message || 'The OpenAI API key is invalid or expired. Check your configuration.' },
  };
  const hc = healthConfig[healthStatus];
  const isNotetakerView = usageSource === 'notetaker';

  return (
    <div className="bg-gray-100 dark:bg-gray-950 transition-[background-color] duration-250 w-full flex flex-col min-h-full">
      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 w-full">

        {/* ── Page Header ── */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{isNotetakerView ? 'Notetaker AI Usage' : 'AI Usage & Billing'}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{isNotetakerView ? 'Track transcription and interview-analysis usage from the notetaker service.' : 'See how much AI is being used across the platform and notetaker services.'}</p>
        </div>

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
          <select value={customStart ? -1 : dateRangeIdx} onChange={(e) => { const v = parseInt(e.target.value); if (v >= 0) { setDateRangeIdx(v); setCustomStart(''); setCustomEnd(''); } }} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none">
            {DATE_RANGES.map((r, i) => <option key={i} value={i}>{r.label}</option>)}
            {customStart && <option value={-1}>Custom</option>}
          </select>
          <input type="date" value={customStart || dateRange.start} onChange={(e) => { setCustomStart(e.target.value); if (!customEnd) setCustomEnd(formatDate(new Date())); }} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={customEnd || dateRange.end} onChange={(e) => { setCustomEnd(e.target.value); if (!customStart) setCustomStart(formatDate(daysAgo(30))); }} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none" />
          {!isNotetakerView && (
            <>
              <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1" />
              <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none min-w-[200px]">
                <option value="">All Companies</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` — ${c.email}` : ''}</option>)}
              </select>
              <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none">
                <option value="">All Sources</option>
                <option value="platform">Platform</option>
                <option value="notetaker">Platform Notetaker</option>
              </select>
              <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none">
                <option value="">All Providers</option>
                <option value="openai">OpenAI</option>
                <option value="openrouter">OpenRouter</option>
                <option value="deepseek">DeepSeek</option>
                <option value="qwen">Qwen / Alibaba</option>
                <option value="moonshot">Moonshot / Kimi</option>
                <option value="groq">Groq</option>
                <option value="anthropic">Anthropic</option>
                <option value="together">Together</option>
              </select>
            </>
          )}
          <button onClick={exportToExcel} className="ml-auto text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Export CSV
          </button>
        </div>

        {/* Company selected banner */}
        {!isNotetakerView && selCompany && (
          <div className="mb-5 px-4 py-3 bg-blue-50 dark:bg-blue-900/15 rounded-xl border border-blue-200 dark:border-blue-800 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: primaryColor }}>{selCompany.name?.[0]?.toUpperCase() || '?'}</div>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{selCompany.name}</p>
              {selCompany.email && <p className="text-xs text-gray-500 dark:text-gray-400">{selCompany.email}</p>}
            </div>
            <button onClick={() => setCompanyFilter('')} className="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:underline">Clear filter</button>
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="flex gap-1 mb-6 bg-white dark:bg-gray-900 rounded-xl p-1 border border-gray-200 dark:border-gray-800 w-fit">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'analysis', label: 'Insights' },
            { key: 'cycles', label: 'Billing Cycles' },
            { key: 'entries', label: 'All Requests' },
          ].map((tab) => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === tab.key ? 'text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`} style={activeTab === tab.key ? { backgroundColor: primaryColor } : {}}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ── OVERVIEW ── */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="space-y-6">

            {/* Health status banner */}
            <div className={`rounded-2xl border ${hc.border} ${hc.bg} p-5 flex items-center gap-4`}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${hc.color}20` }}>
                {healthStatus === 'healthy' ? (
                  <svg className="w-6 h-6" style={{ color: hc.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : (healthStatus === 'no_credits' || healthStatus === 'key_error') ? (
                  <svg className="w-6 h-6" style={{ color: hc.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                ) : healthStatus === 'loading' ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300" style={{ borderTopColor: hc.color }} />
                ) : (
                  <svg className="w-6 h-6" style={{ color: hc.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                )}
              </div>
              <div>
                <p className="text-base font-semibold text-gray-900 dark:text-white">{hc.label}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{hc.desc}</p>
              </div>
            </div>

            {/* Big 3 cards: Cost, Requests, Success Rate */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}CC)` }}>
                <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10" />
                <p className="text-sm text-white/70 mb-1">Total Spent</p>
                {loading ? <div className="h-10 w-24 bg-white/20 rounded animate-pulse" /> : (
                  <p className="text-4xl font-bold">${(summary?.totals?.cost ?? 0).toFixed(2)}</p>
                )}
                <p className="text-xs text-white/50 mt-2">Estimated OpenAI charges</p>
              </div>

              <StatCard
                label="Total AI Requests"
                value={summary?.totals?.calls ?? 0}
                isLoading={loading}
                subtitle={`${summary?.totals?.tokens?.toLocaleString() ?? 0} tokens processed`}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
              />

              <StatCard
                label="Success Rate"
                value={summary?.totals?.calls > 0 ? `${((1 - ((summary.totals.errors + summary.totals.quota_exceeded) / summary.totals.calls)) * 100).toFixed(1)}%` : '—'}
                isLoading={loading}
                subtitle={`${summary?.totals?.errors ?? 0} failed, ${summary?.totals?.quota_exceeded ?? 0} no credits`}
                color={summary?.totals?.errors > 0 ? '#F59E0B' : '#10B981'}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              />
            </div>

            {/* Where is the money going? */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Where is AI being used?</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Breakdown by feature</p>
                <DonutChart data={endpointDonutData} title="" size={160} strokeWidth={24} />
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Which AI model?</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">GPT-4 costs more than GPT-4o-mini</p>
                <DonutChart data={modelDonutData} title="" size={160} strokeWidth={24} />
              </div>
              {!isNotetakerView && sourceDonutData.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">By Source</h3>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Platform usage compared with platform notetaker usage</p>
                  <DonutChart data={sourceDonutData} title="" size={160} strokeWidth={24} />
                </div>
              )}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">By Provider</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Usage across different LLM providers</p>
                <DonutChart data={providerDonutData} title="" size={160} strokeWidth={24} />
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Input vs Output</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Text sent to AI vs text received back</p>
                <DonutChart data={tokenSplitData} title="" size={160} strokeWidth={24} />
              </div>
            </div>

            {/* Daily usage chart */}
            {summary?.daily_trend?.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Daily Activity</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">AI requests per day — taller bars = more usage</p>
                <div className="flex items-end gap-[2px] h-24 mb-2">
                  {summary.daily_trend.map((day) => {
                    const maxCalls = Math.max(...summary.daily_trend.map((d) => d.calls), 1);
                    const pct = (day.calls / maxCalls) * 100;
                    return (
                      <div key={day.date} className="flex-1 rounded-t transition-all duration-300 hover:opacity-80 group relative" style={{ height: `${Math.max(pct, 2)}%`, backgroundColor: primaryColor, minWidth: '4px' }}>
                        <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 dark:bg-gray-700 text-white text-xs px-2.5 py-1.5 rounded-lg whitespace-nowrap z-10 shadow-lg">
                          <strong>{fmtDate(day.date)}</strong><br />{day.calls} requests &middot; ${day.cost.toFixed(4)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-gray-400">{fmtDate(summary.daily_trend[0]?.date)}</span>
                  <span className="text-xs text-gray-400">{fmtDate(summary.daily_trend.at(-1)?.date)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ── INSIGHTS (Analysis) ── */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'analysis' && (
          <div className="space-y-6">
            {loading || !analysisData ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300" style={{ borderTopColor: primaryColor }} /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard label="Requests / Day" value={analysisData.avgCallsPerDay} subtitle={`avg over ${analysisData.days} days`} icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>} />
                  <StatCard label="Tokens / Request" value={analysisData.avgTokensPerCall.toLocaleString()} subtitle="average" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>} />
                  <StatCard label="Daily Cost" value={`$${analysisData.avgCostPerDay.toFixed(4)}`} subtitle="average" gradient icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
                  <StatCard label="Tokens / Dollar" value={analysisData.tokensPerDollar.toLocaleString()} subtitle="value for money" icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Busiest day */}
                  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Peak Usage</h3>
                    {analysisData.peakDay ? (
                      <div className="space-y-4">
                        <div><p className="text-xs text-gray-400 mb-0.5">Busiest day</p><p className="text-lg font-bold text-gray-900 dark:text-white">{fmtDate(analysisData.peakDay)}</p><p className="text-sm text-gray-500">{analysisData.peakCalls.toLocaleString()} requests</p></div>
                        <div><p className="text-xs text-gray-400 mb-0.5">Most used feature</p><p className="text-lg font-bold text-gray-900 dark:text-white">{analysisData.topEndpoint ? friendlyEndpoint(analysisData.topEndpoint.endpoint) : '—'}</p><p className="text-sm text-gray-500">{(analysisData.topEndpoint?.calls || 0).toLocaleString()} requests</p></div>
                        <div><p className="text-xs text-gray-400 mb-0.5">Most used model</p><p className="text-lg font-bold text-gray-900 dark:text-white font-mono">{analysisData.topModel?.model || '—'}</p></div>
                      </div>
                    ) : <p className="text-sm text-gray-400">No data</p>}
                  </div>

                  {/* Reliability */}
                  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Reliability</h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Successful', val: (summary.totals.calls - summary.totals.errors - summary.totals.quota_exceeded), color: '#10B981', pct: summary.totals.calls > 0 ? ((1 - (summary.totals.errors + summary.totals.quota_exceeded) / summary.totals.calls) * 100).toFixed(1) : '0' },
                        { label: 'Failed', val: summary.totals.errors, color: '#EF4444', pct: summary.totals.calls > 0 ? ((summary.totals.errors / summary.totals.calls) * 100).toFixed(1) : '0' },
                        { label: 'No Credits', val: summary.totals.quota_exceeded, color: '#F59E0B', pct: summary.totals.calls > 0 ? ((summary.totals.quota_exceeded / summary.totals.calls) * 100).toFixed(1) : '0' },
                      ].map((r) => (
                        <div key={r.label}>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm text-gray-500">{r.label} <span className="text-xs text-gray-400">({r.val})</span></span>
                            <span className="text-sm font-medium" style={{ color: r.color }}>{r.pct}%</span>
                          </div>
                          <ProportionBar value={r.val} max={summary.totals.calls} color={r.color} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Cost by feature + model */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {analysisData.endpointsByCost.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Cost by Feature</h3>
                      <p className="text-xs text-gray-400 mb-4">Which AI features cost the most</p>
                      <div className="space-y-3">
                        {analysisData.endpointsByCost.map((ep, i) => (
                          <div key={ep.endpoint}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1 mr-3">{friendlyEndpoint(ep.endpoint)}</span>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-xs text-gray-400">{ep.calls.toLocaleString()} req</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">${ep.cost.toFixed(4)}</span>
                              </div>
                            </div>
                            <ProportionBar value={ep.cost} max={analysisData.endpointsByCost[0]?.cost || 1} color={i === 0 ? primaryColor : `${primaryColor}80`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisData.modelsByCost.length > 0 && (
                    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Cost by Model</h3>
                      <p className="text-xs text-gray-400 mb-4">Which AI models cost the most</p>
                      <div className="space-y-3">
                        {analysisData.modelsByCost.map((m, i) => (
                          <div key={m.model}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-xs text-gray-700 dark:text-gray-300 font-mono truncate flex-1 mr-3">{m.model}</span>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-xs text-gray-400">{m.calls.toLocaleString()} req</span>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">${m.cost.toFixed(4)}</span>
                              </div>
                            </div>
                            <ProportionBar value={m.cost} max={analysisData.modelsByCost[0]?.cost || 1} color={i === 0 ? secondaryColor : `${secondaryColor}80`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ── BILLING CYCLES ── */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'cycles' && (
          <div className="space-y-6">
            {cyclesLoading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300" style={{ borderTopColor: primaryColor }} /></div>
            ) : cyclesData?.cycles?.length > 0 ? (
              <>
                {/* Active cycle card */}
                {(() => {
                  const active = cyclesData.cycles.find((c) => c.is_active);
                  if (!active) return (
                    <div className="rounded-2xl border-2 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10 p-6 text-center">
                      <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-3">
                        <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                      </div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white mb-1">Credits Have Run Out</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Your OpenAI balance is empty. Add credits to resume AI features.</p>
                    </div>
                  );
                  const days = active.duration_hours != null ? Math.round(active.duration_hours / 24) : '?';
                  return (
                    <div className="rounded-2xl border-2 border-green-300 dark:border-green-700 bg-white dark:bg-gray-900 p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <span className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                        </div>
                        <div>
                          <p className="text-base font-bold text-gray-900 dark:text-white">Current Billing Cycle</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Started {fmtDate(active.started_at)} &middot; {days} day{days !== 1 ? 's' : ''} ago</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{fmtCost(active.cost)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Spent so far</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{active.calls.toLocaleString()}</p>
                          <p className="text-xs text-gray-500 mt-0.5">AI requests made</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{active.calls > 0 ? ((active.success_calls / active.calls) * 100).toFixed(0) : 0}%</p>
                          <p className="text-xs text-gray-500 mt-0.5">Success rate</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{active.tokens.toLocaleString()}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Tokens used</p>
                        </div>
                      </div>

                      {/* Feature usage bars */}
                      {active.by_endpoint?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Spending by feature</p>
                          <div className="space-y-2">
                            {active.by_endpoint.slice(0, 5).map((ep) => (
                              <div key={ep.endpoint}>
                                <div className="flex justify-between items-center mb-0.5">
                                  <span className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1 mr-3">{friendlyEndpoint(ep.endpoint)}</span>
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">${ep.cost.toFixed(4)}</span>
                                </div>
                                <ProportionBar value={ep.cost} max={active.by_endpoint[0]?.cost || 1} color={primaryColor} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Spending timeline chart */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Spending Timeline</h3>
                  <p className="text-xs text-gray-400 mb-4">How much was spent each time you added credits</p>
                  <div className="flex items-end gap-3 h-36">
                    {[...cyclesData.cycles].reverse().map((c) => {
                      const maxCost = Math.max(...cyclesData.cycles.map((x) => x.cost), 0.01);
                      const pct = (c.cost / maxCost) * 100;
                      return (
                        <div key={c.cycle_number} className="flex-1 flex flex-col items-center gap-1 group relative min-w-[40px]">
                          <span className="text-xs font-medium text-gray-500">{fmtCost(c.cost)}</span>
                          <div className="w-full rounded-t-lg transition-all duration-300 hover:opacity-80" style={{ height: `${Math.max(pct, 4)}%`, backgroundColor: c.is_active ? '#10B981' : primaryColor, minHeight: '6px' }}>
                            <div className="absolute bottom-full mb-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap z-10 shadow-lg">
                              <strong>Cycle #{c.cycle_number}</strong><br />{c.calls} requests &middot; {c.tokens.toLocaleString()} tokens<br />{c.is_active ? 'Currently active' : 'Credits ran out'}
                            </div>
                          </div>
                          <span className="text-[10px] text-gray-400">{fmtDate(c.started_at)}</span>
                          {c.is_active ? (
                            <span className="text-[10px] font-semibold text-green-500">ACTIVE</span>
                          ) : (
                            <span className="text-[10px] text-red-400">ENDED</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Cycle history table */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Billing History</h3>
                  <p className="text-xs text-gray-400 mb-4">Click a row to see which features were used</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-800">
                          <th className="pb-2 pr-4 font-medium">#</th>
                          <th className="pb-2 pr-4 font-medium">Started</th>
                          <th className="pb-2 pr-4 font-medium">Ended</th>
                          <th className="pb-2 pr-4 font-medium text-right">Duration</th>
                          <th className="pb-2 pr-4 font-medium text-right">Requests</th>
                          <th className="pb-2 pr-4 font-medium text-right">Tokens</th>
                          <th className="pb-2 pr-4 font-medium text-right">Spent</th>
                          <th className="pb-2 pr-4 font-medium text-right">Failed</th>
                          <th className="pb-2 pr-4 font-medium">Status</th>
                          <th className="pb-2 font-medium text-right">vs Prev</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cyclesData.cycles.map((c, i) => {
                          const prev = cyclesData.cycles[i + 1];
                          let costChange = (prev && prev.cost > 0) ? ((c.cost - prev.cost) / prev.cost) * 100 : null;
                          const isExp = expandedCycle === c.cycle_number;
                          return (
                            <React.Fragment key={c.cycle_number}>
                              <tr className={`border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer ${c.is_active ? 'bg-green-50/50 dark:bg-green-900/10' : ''}`} onClick={() => setExpandedCycle(isExp ? null : c.cycle_number)}>
                                <td className="py-2.5 pr-4 font-medium text-gray-500">{c.cycle_number}</td>
                                <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300 text-xs whitespace-nowrap">{fmtDateTime(c.started_at)}</td>
                                <td className="py-2.5 pr-4 text-gray-700 dark:text-gray-300 text-xs whitespace-nowrap">{c.ended_at ? fmtDateTime(c.ended_at) : '—'}</td>
                                <td className="py-2.5 pr-4 text-right text-gray-500">{c.duration_hours != null ? (c.duration_hours >= 24 ? `${Math.round(c.duration_hours / 24)} days` : `${c.duration_hours.toFixed(1)} hrs`) : '—'}</td>
                                <td className="py-2.5 pr-4 text-right font-medium text-gray-900 dark:text-white">{c.calls.toLocaleString()}</td>
                                <td className="py-2.5 pr-4 text-right text-gray-700 dark:text-gray-300">{c.tokens.toLocaleString()}</td>
                                <td className="py-2.5 pr-4 text-right font-medium text-gray-900 dark:text-white">{fmtCost(c.cost)}</td>
                                <td className="py-2.5 pr-4 text-right text-gray-500">{c.error_calls}</td>
                                <td className="py-2.5 pr-4">
                                  {c.is_active ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"><span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />Active</span>
                                  ) : (
                                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Credits Used Up</span>
                                  )}
                                </td>
                                <td className="py-2.5 text-right">
                                  {c.is_active ? (
                                    <span className="text-xs text-gray-400">Just started</span>
                                  ) : costChange !== null ? (
                                    <span className={`text-sm font-medium ${costChange > 0 ? 'text-red-500' : costChange < 0 ? 'text-green-500' : 'text-gray-400'}`}>{costChange > 0 ? '+' : ''}{costChange.toFixed(1)}%</span>
                                  ) : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                              </tr>
                              {isExp && (
                                <tr><td colSpan={10} className="p-4 bg-gray-50 dark:bg-gray-800/40">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {c.by_endpoint?.length > 0 && (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-medium">By Feature</p>
                                        {c.by_endpoint.map((ep) => (
                                          <div key={ep.endpoint} className="flex justify-between items-center text-xs py-1">
                                            <span className="text-gray-600 dark:text-gray-400 truncate flex-1 mr-2">{friendlyEndpoint(ep.endpoint)}</span>
                                            <div className="flex gap-3 flex-shrink-0"><span className="text-gray-400">{ep.calls} req</span><span className="font-medium text-gray-900 dark:text-white">${ep.cost.toFixed(4)}</span></div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {c.by_model?.length > 0 && (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-medium">By Model</p>
                                        {c.by_model.map((m, idx) => (
                                          <div key={`${m.provider || ''}_${m.model}_${idx}`} className="flex justify-between items-center text-xs py-1">
                                            <span className="text-gray-600 dark:text-gray-400 font-mono truncate flex-1 mr-2">
                                              {m.provider && <span className="text-gray-400 mr-1">{m.provider}</span>}
                                              {m.model}
                                            </span>
                                            <div className="flex gap-3 flex-shrink-0"><span className="text-gray-400">{m.calls} req</span><span className="font-medium text-gray-900 dark:text-white">${(m.cost || 0).toFixed(4)}</span></div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </td></tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <p className="text-base font-medium text-gray-600 dark:text-gray-300 mb-1">No billing cycles yet</p>
                <p className="text-sm text-gray-400">Billing cycles are created automatically each time your OpenAI credits run out and you add more.</p>
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* ── ALL REQUESTS ── */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab === 'entries' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <input type="text" placeholder="Filter by feature name..." value={endpointFilter} onChange={(e) => setEndpointFilter(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none w-52" />
              <input type="text" placeholder="Filter by model..." value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none w-44" />
              <button onClick={() => fetchEntries(1)} className="text-sm px-4 py-2 rounded-lg text-white" style={{ backgroundColor: primaryColor }}>Apply</button>
              <span className="text-sm text-gray-400 ml-auto">{entriesTotal.toLocaleString()} requests found</span>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Company</th>
                      <th className="px-4 py-3 font-medium">Source</th>
                      <th className="px-4 py-3 font-medium">Feature</th>
                      <th className="px-4 py-3 font-medium">Model</th>
                      <th className="px-4 py-3 font-medium text-right">Tokens</th>
                      <th className="px-4 py-3 font-medium text-right">Cost</th>
                      <th className="px-4 py-3 font-medium text-right">Duration</th>
                      <th className="px-4 py-3 font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entriesLoading ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                    ) : entries.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No requests found for the selected filters</td></tr>
                    ) : (
                      entries.map((entry, idx) => {
                        const comp = getCompanyInfo(entry.company_id);
                        const source = normalizeUsageSource(entry);
                        return (
                          <tr key={idx} className="border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                            <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">{fmtDateTime(entry.ts)}</td>
                            <td className="px-4 py-2.5">
                              <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{entry.company_name || comp.name}</p>
                              {comp.email && <p className="text-[10px] text-gray-400">{comp.email}</p>}
                            </td>
                            <td className="px-4 py-2.5"><SourceBadge source={source} /></td>
                            <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300">{friendlyEndpoint(entry.endpoint || '')}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300 font-mono">{entry.provider && <span className="text-gray-400 mr-1">{entry.provider}</span>}{entry.model || '—'}</td>
                            <td className="px-4 py-2.5 text-right font-medium text-gray-900 dark:text-white">{(entry.total_tokens ?? 0).toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">${(entry.cost ?? 0).toFixed(4)}</td>
                            <td className="px-4 py-2.5 text-right text-xs text-gray-500" title={entry.started_at && entry.ended_at ? `${fmtDateTime(entry.started_at)} → ${fmtDateTime(entry.ended_at)}` : ''}>{fmtDurationSeconds(entry)}</td>
                            <td className="px-4 py-2.5"><StatusBadge status={entry.status} /></td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <button onClick={() => fetchEntries(entriesPage - 1)} disabled={entriesPage <= 1} className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">Previous</button>
                  <span className="text-sm text-gray-500">Page {entriesPage} of {totalPages}</span>
                  <button onClick={() => fetchEntries(entriesPage + 1)} disabled={entriesPage >= totalPages} className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800">Next</button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
